import { ApiErrors, QueryResult } from '@betterment/js-runtime';
import { Box, Card, H2, HStack, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { take as _take, times as _times } from 'es-toolkit/compat';

import { useAccount } from './AccountProvider';
import TransactionLoader from './TransactionLoader';
import TransactionsTable from './TransactionsTable';

import ErrorFallback from '../../Shared/components/ErrorFallback';
import { urlBuilder } from '../../Shared/urlBuilder';
import { DetailedTransaction } from '../data';

export interface Props {
  errors: ApiErrors | null;
  loadData: QueryResult<DetailedTransaction[]>['refetch'];
  isLoading: boolean;
  transactions: DetailedTransaction[] | null;
}

export const TRANSACTION_SUMMARY_LIMIT = 6;

const SummaryTransactions: React.FC<React.PropsWithChildren<Props>> = ({
  errors,
  loadData,
  isLoading,
  transactions,
}) => {
  const shouldShowViewAllLink = transactions && transactions.length > TRANSACTION_SUMMARY_LIMIT;
  const limitedTransactions = _take(transactions, TRANSACTION_SUMMARY_LIMIT);
  const shouldShowNoTransactions = !isLoading && transactions && transactions.length === 0;
  const { account } = useAccount();

  return (
    <Card
      css={{
        px: 'space.300',
        mb: { base: 'space.200', sm: 'space.300' },
      }}
    >
      <HStack
        css={{
          justifyContent: 'space-between',
          pb: 'space.200',
          pt: 'space.300',
          borderBottomWidth: 'thin',
          borderBottomStyle: 'solid',
          borderBottomColor: 'border.weak',
        }}
      >
        <H2 variant="title200" css={{ m: 'space.0' }}>
          Recent transactions
        </H2>
        {shouldShowViewAllLink && (
          <SambaAutoLink
            to={urlBuilder.getAppCheckingAccountTransactions.absolute({ accountId: account.id })}
            title="View All Transactions"
          >
            View all
          </SambaAutoLink>
        )}
      </HStack>
      {!isLoading && <TransactionsTable transactions={limitedTransactions} />}

      {errors && (
        <Box css={{ pb: 'space.300' }}>
          <ErrorFallback message="Unable to load transactions." handleReload={loadData} />
        </Box>
      )}

      {shouldShowNoTransactions && (
        <Box css={{ px: { base: 'space.200', md: 'space.300' }, py: '135px' }}>
          <Text as="p" css={{ fontWeight: 'bold', textAlign: 'center', mb: 'space.100' }}>
            You have no transactions
          </Text>
          <Text as="p" css={{ textStyle: 'text50', textAlign: 'center', color: 'text.secondary' }}>
            Fund your account to start spending.
          </Text>
        </Box>
      )}

      {isLoading && (
        <Box>
          {_times(TRANSACTION_SUMMARY_LIMIT, value => (
            <TransactionLoader key={value} />
          ))}
        </Box>
      )}
    </Card>
  );
};

export default SummaryTransactions;
